/**
 * SA demo-tenant list query hook (B-11).
 *
 * Endpoint: GET /admin/demo
 *
 * Returns all demo tenants (is_demo=true) with aggregate counts.
 * Used by the ``/demos`` SA dashboard list page.
 */
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { ApiError } from "@/lib/apiError";

interface DemoListItem {
  tenant_id: string;
  slug: string;
  created_at: string;
  last_reset_at: string | null;
  admin_email: string | null;
  candidate_count: number;
  jd_count: number;
  ranking_count: number;
  expected_jd_count: number;
  expected_candidate_count: number;
  expected_ranking_count: number;
  /**
   * False when the tenant is HALF-SEEDED. A demo reset is a >4-minute
   * synchronous run ending in a live-AI loop; if it dies partway the
   * wipe+provision has already committed, so the broken tenant stays live and
   * login-able with unremarkable-looking counts. Compare against the
   * `expected_*` fields to show the shortfall.
   */
  seed_complete: boolean;
}

interface DemoListResponse {
  demos: DemoListItem[];
  total_count: number;
}

export function useAdminDemos() {
  return useQuery<DemoListResponse, ApiError>({
    queryKey: ["admin", "demos"],
    queryFn: async () => {
      const r = await apiClient.get<DemoListResponse>("/admin/demo");
      return r.data;
    },
  });
}
